import type { ReactNode } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";

export interface ConfirmDialogProps {
  /** Whether the dialog is shown. Defaults to true, so `{pending && <ConfirmDialog …/>}` reads naturally. */
  open?: boolean;
  /** Dialog heading — name the thing being acted on, not just the verb. */
  title: string;
  /** What the action will do, and anything the user cannot undo afterwards. */
  children: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /** Marks the confirm button destructive. Use for anything that removes or revokes. */
  danger?: boolean;
  /** Disables both buttons while the action is in flight, so it cannot be fired twice. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Are you sure?" — one dialog, so every confirmation in the app agrees on button order and shape.
 *
 * <p>The app had accumulated more than a dozen hand-rolled versions of this, and they had already
 * drifted: a count of every modal footer found 56 with Cancel first and 2 with the confirm first.
 * Cancel-first is the majority and the safer default (the destructive button is not under the cursor's
 * resting position), so it is baked in here rather than left to each caller.
 *
 * <p>Labels are props rather than translated internally: this package carries no i18n, exactly as the
 * other components here do.
 */
export function ConfirmDialog({
  open = true,
  title,
  children,
  confirmLabel,
  cancelLabel,
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal open={open} title={title} onClose={onCancel} footer={
      <>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={busy}
          className={danger ? "fui-confirm__danger" : undefined}
        >
          {confirmLabel}
        </Button>
      </>
    }>
      <p className="fui-confirm__body">{children}</p>
    </Modal>
  );
}
